import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchScheduleServices,
  createScheduleService,
  updateScheduleService,
  deleteScheduleService,
} from "./thunks";
import { Service } from "@/types";
import { toast } from "react-toastify";

interface PaginatedServices {
  data: Service[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface ScheduleServiceState {
  services: PaginatedServices;
  loading: boolean;
  error: string | null;
}

const initialState: ScheduleServiceState = {
  services: { data: [] },
  loading: false,
  error: null,
};

const scheduleServiceSlice = createSlice({
  name: "scheduleServices",
  initialState,
  reducers: {
    clearScheduleServices: (state) => {
      state.services = { data: [] };
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchScheduleServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchScheduleServices.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            state.services = { data: payload.data };
          } else {
            state.services = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchScheduleServices.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })

      // Create
      .addCase(createScheduleService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createScheduleService.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.services.data.push(action.payload.data);
          toast.success("Serviço adicionado com sucesso!");
        },
      )
      .addCase(createScheduleService.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao adicionar serviço!");
      })

      // Update
      .addCase(updateScheduleService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateScheduleService.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.services.data.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (index !== -1) {
            state.services.data[index] = action.payload.data;
          }
          toast.success("Serviço atualizado com sucesso!");
        },
      )
      .addCase(updateScheduleService.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao atualizar serviço!");
      })

      // Delete
      .addCase(deleteScheduleService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteScheduleService.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.services.data = state.services.data.filter(
            (s) => String(s.id) !== String(action.payload.serviceId),
          );
          toast.success("Serviço removido com sucesso!");
        },
      )
      .addCase(deleteScheduleService.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao remover serviço!");
      });
  },
});

export const { clearScheduleServices } = scheduleServiceSlice.actions;
export default scheduleServiceSlice.reducer;
